import { useState } from 'react';
import { FieldLabel } from '../ui/FieldLabel';
import { PasswordInput } from '../ui/PasswordInput';
import { channelFieldHelp } from '../../pages/customerSuccess/channelFieldHelp';
import {
  SupportChannelWebhookSettingsUpsert,
  SupportChannelWebhookStatusMapEntry,
} from '../../services/supportChannelsApi';
import { TICKET_STAGES, TicketStageId } from '../../types/TicketStage';

type SupportChannelWebhookPanelProps = {
  value: SupportChannelWebhookSettingsUpsert;
  hasAuthSecret: boolean;
  onChange: (value: SupportChannelWebhookSettingsUpsert) => void;
};

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

export function SupportChannelWebhookPanel({
  value,
  hasAuthSecret,
  onChange,
}: SupportChannelWebhookPanelProps) {
  const [open, setOpen] = useState(value.enabled);
  const authType = value.authType || 'None';

  function patch(next: Partial<SupportChannelWebhookSettingsUpsert>): void {
    onChange({ ...value, ...next });
  }

  function patchStatus(index: number, next: Partial<SupportChannelWebhookStatusMapEntry>): void {
    const statusMap = value.statusMap.map((entry, entryIndex) =>
      entryIndex === index ? { ...entry, ...next } : entry,
    );
    patch({ statusMap });
  }

  function addStatus(): void {
    const used = new Set(value.statusMap.map((entry) => entry.goStatus));
    const available = TICKET_STAGES.find((stage) => !used.has(stage.id));
    patch({
      statusMap: [
        ...value.statusMap,
        { goStatus: available?.id ?? 'Resolved', externalStatus: '' },
      ],
    });
  }

  function removeStatus(index: number): void {
    patch({ statusMap: value.statusMap.filter((_, entryIndex) => entryIndex !== index) });
  }

  return (
    <section className="rounded-lg border border-secondary-200 bg-white">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        onClick={() => setOpen((current) => !current)}
      >
        <span>
          <span className="block text-sm font-medium text-secondary-900">Webhook de atualização</span>
          <span className="block text-xs text-secondary-500">
            {value.enabled && value.url ? value.url : 'Desligado · request HTTP ao atualizar o chamado'}
          </span>
        </span>
        <span className="text-xs text-secondary-500">{open ? 'Recolher' : 'Expandir'}</span>
      </button>

      {open ? (
        <div className="space-y-4 border-t border-secondary-100 px-4 py-4">
          <label className="flex items-center gap-2 text-sm text-secondary-800">
            <input
              type="checkbox"
              checked={value.enabled}
              onChange={(event) => patch({ enabled: event.target.checked })}
            />
            <FieldLabel {...channelFieldHelp.webhookEnabled}>Disparar webhook</FieldLabel>
          </label>

          <div className="erp-form-grid">
            <label className="erp-label">
              <FieldLabel {...channelFieldHelp.assistantMethod}>Método</FieldLabel>
              <select
                className="erp-input"
                value={value.method || 'POST'}
                onChange={(event) => patch({ method: event.target.value })}
              >
                {METHODS.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label md:col-span-2">
              <FieldLabel {...channelFieldHelp.webhookUrl}>URL</FieldLabel>
              <input
                className="erp-input"
                value={value.url ?? ''}
                onChange={(event) => patch({ url: event.target.value })}
                placeholder="https://"
              />
            </label>
          </div>

          <div className="erp-form-grid">
            <label className="erp-label">
              <FieldLabel {...channelFieldHelp.assistantAuthType}>Autenticação</FieldLabel>
              <select
                className="erp-input"
                value={authType}
                onChange={(event) => patch({ authType: event.target.value })}
              >
                <option value="None">Nenhuma</option>
                <option value="Bearer">Bearer</option>
                <option value="Basic">Basic</option>
                <option value="ApiKey">API Key</option>
              </select>
            </label>
            {authType === 'Basic' ? (
              <label className="erp-label">
                <FieldLabel {...channelFieldHelp.assistantAuthUsername}>Usuário</FieldLabel>
                <input
                  className="erp-input"
                  value={value.authUsername ?? ''}
                  onChange={(event) => patch({ authUsername: event.target.value })}
                />
              </label>
            ) : null}
            {authType === 'ApiKey' ? (
              <label className="erp-label">
                <FieldLabel {...channelFieldHelp.assistantApiKeyHeader}>Header da API key</FieldLabel>
                <input
                  className="erp-input"
                  value={value.apiKeyHeaderName ?? ''}
                  onChange={(event) => patch({ apiKeyHeaderName: event.target.value })}
                  placeholder="X-Api-Key"
                />
              </label>
            ) : null}
            {authType !== 'None' ? (
              <label className="erp-label">
                <FieldLabel {...channelFieldHelp.assistantAuthSecret}>Secret / token</FieldLabel>
                <PasswordInput
                  className="erp-input pr-11 !mt-0"
                  wrapperClassName="mt-1 w-full"
                  autoComplete="new-password"
                  value={value.authSecret ?? ''}
                  onChange={(event) => patch({ authSecret: event.target.value })}
                  placeholder={hasAuthSecret ? 'Deixe em branco para manter' : undefined}
                />
              </label>
            ) : null}
          </div>

          <label className="erp-label">
            <FieldLabel {...channelFieldHelp.assistantHeaders}>Headers (JSON)</FieldLabel>
            <textarea
              className="erp-input min-h-24 font-mono text-xs"
              value={value.headersJson ?? ''}
              onChange={(event) => patch({ headersJson: event.target.value })}
            />
          </label>

          <label className="erp-label">
            <FieldLabel {...channelFieldHelp.webhookBody}>Corpo</FieldLabel>
            <textarea
              className="erp-input min-h-40 font-mono text-xs"
              value={value.bodyTemplate ?? ''}
              onChange={(event) => patch({ bodyTemplate: event.target.value })}
            />
          </label>

          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <FieldLabel {...channelFieldHelp.webhookStatusMap}>Mapa de status</FieldLabel>
              <button type="button" className="erp-btn-secondary" onClick={addStatus}>
                + Status
              </button>
            </div>
            {value.statusMap.length === 0 ? (
              <p className="text-xs text-secondary-500">
                Sem conversão: o webhook envia o nome do status do Go.
              </p>
            ) : (
              <div className="space-y-2">
                {value.statusMap.map((entry, index) => (
                  <div key={`${entry.goStatus}-${index}`} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                    <select
                      className="erp-input"
                      value={entry.goStatus}
                      onChange={(event) =>
                        patchStatus(index, { goStatus: event.target.value as TicketStageId })
                      }
                    >
                      {TICKET_STAGES.map((stage) => (
                        <option key={stage.id} value={stage.id}>
                          {stage.label}
                        </option>
                      ))}
                    </select>
                    <input
                      className="erp-input"
                      value={entry.externalStatus}
                      onChange={(event) => patchStatus(index, { externalStatus: event.target.value })}
                      placeholder="Status no outro sistema"
                    />
                    <button
                      type="button"
                      className="erp-btn-secondary"
                      onClick={() => removeStatus(index)}
                    >
                      Remover
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
